/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СПРАВОЧНИК РОЛЕЙ УЧАСТНИКОВ текущего мероприятия
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** «Спортсмен», «тренер», «представитель команды» —
 * это то, кем человек приехал на соревнование. Ни одна проверка прав в этом
 * приложении не имеет права смотреть сюда: за права отвечают `auth.isAdmin`
 * (система) и `event.canManage` (мероприятие).
 *
 * Соблазн реален: слово «роль» здесь и в `MemberRole` одно и то же, а
 * `participantRoleId` лежит в двух шагах от кнопки «Удалить». Именно поэтому
 * тип называется `ParticipantRole`, а не `Role`, и живёт в отдельном сторе.
 *
 * Справочник локален для мероприятия и пагинации не имеет: это десятки записей
 * для выпадающего списка, а не лента.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { participantRolesApi } from '@/api/participant-roles'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import type { ParticipantRole } from '@/types/api'

export const useParticipantRolesStore = defineStore('participantRoles', () => {
  /* ───────────────────────── state ───────────────────────── */

  const eventId = ref<string | null>(null)

  /** Отсортирован сервером по алфавиту. */
  const items = ref<ParticipantRole[]>([])

  const loading = ref(false)
  const error = shallowRef<ApiError | null>(null)
  const ready = ref(false)

  /* ───────────────────────── getters ─────────────────────── */

  const isEmpty = computed(() => items.value.length === 0)

  const byId = computed(() => new Map(items.value.map((role) => [role.id, role])))

  /**
   * Название роли по id.
   *
   * Возвращает `''` для пустого id (роль не назначена — штатное состояние, а не
   * ошибка) и для id, которого в справочнике нет: роль могли удалить, пока
   * экран был открыт, и на сервере ссылка у участника при этом **обнуляется**
   * (`ON DELETE SET NULL`), а не оставляет висячий id. Падать здесь нечему.
   */
  function nameOf(roleId: string): string {
    if (!roleId) return ''
    return byId.value.get(roleId)?.name ?? ''
  }

  /* ───────────────────────── запрос ──────────────────────── */

  let controller: AbortController | null = null

  async function load(id: string, force = false): Promise<void> {
    if (!force && eventId.value === id && ready.value && error.value === null) return

    controller?.abort()
    const ctl = new AbortController()
    controller = ctl

    if (eventId.value !== id) {
      items.value = []
      ready.value = false
    }

    eventId.value = id
    loading.value = true
    error.value = null

    try {
      const data = await participantRolesApi.list(id, ctl.signal)
      if (controller !== ctl) return
      items.value = data.participantRoles
    } catch (err) {
      if (controller !== ctl) return

      const apiError = normalizeError(err)
      if (apiError.isCanceled) return

      error.value = apiError
      items.value = []
    } finally {
      if (controller === ctl) {
        controller = null
        loading.value = false
        ready.value = true
      }
    }
  }

  /* ───────────────────────── мутации ─────────────────────── */

  /** Бросают `ApiError`; вызывающий заворачивает в `useAsyncAction`. */

  async function create(
    name: string,
    nameSecondLanguage: string,
    color: string,
  ): Promise<ParticipantRole> {
    const id = requireEventId()
    const response = await participantRolesApi.create(id, { name, nameSecondLanguage, color })
    await load(id, true)
    return response.participantRole
  }

  /**
   * Название, его перевод и цвет — все три поля разом.
   *
   * Это PUT, и ни цвет, ни перевод не бывают «не переданы»: пустая строка
   * означает «не задано», а не «оставить как было». Вызывающий обязан передать
   * текущие значения, если менял только одно из них.
   */
  async function update(
    roleId: string,
    name: string,
    nameSecondLanguage: string,
    color: string,
  ): Promise<ParticipantRole> {
    const id = requireEventId()
    const response = await participantRolesApi.update(id, roleId, {
      name,
      nameSecondLanguage,
      color,
    })
    await load(id, true)
    return response.participantRole
  }

  /**
   * Набор зон роли целиком.
   *
   * ⚠️ Отдельная мутация от `update`, а не «ещё один аргумент к нему»: одним
   * запросом правка названия отправляла бы вместе с собой весь набор зон и
   * затирала бы чужую одновременную правку набора. Сервер и разделил их именно
   * поэтому.
   *
   * Множество: порядок не значим, повторы схлопнёт сервер. Пустой массив —
   * законное значение, «снять все зоны».
   */
  async function setAccessZones(roleId: string, zoneIds: string[]): Promise<ParticipantRole> {
    const id = requireEventId()
    const response = await participantRolesApi.setAccessZones(id, roleId, {
      accessZoneIds: zoneIds,
    })
    await load(id, true)
    return response.participantRole
  }

  /**
   * Сколько ролей выдают эту зону.
   *
   * Считается ЗДЕСЬ, из уже загруженного справочника, а не отдельным запросом:
   * каждая роль везёт свои `accessZoneIds`, так что ответ у клиента уже есть, и
   * эндпоинт ради него был бы вторым источником правды, который расходится с
   * первым. Ровно тем же способом экран ролей считает поля, уходящие вместе с
   * ролью.
   */
  function rolesUsingZone(zoneId: string): ParticipantRole[] {
    if (!zoneId) return []
    return items.value.filter((role) => role.accessZoneIds.includes(zoneId))
  }

  /**
   * Набор уровней роли целиком.
   *
   * ⚠️ Отдельная мутация и от `update`, и от `setAccessZones`. Уровни зонам не
   * альтернатива: у роли оба набора живут одновременно и правятся разными
   * галочками на одном экране. Отправить их одним запросом значило бы, что
   * щелчок по зоне переписывает уровни (и наоборот) — и что две вкладки,
   * правящие разное, затирают друг друга.
   *
   * Множество: порядок не значим, повторы схлопнёт сервер. Пустой массив —
   * законное значение, «снять все уровни».
   */
  async function setAccessLevels(roleId: string, levelIds: string[]): Promise<ParticipantRole> {
    const id = requireEventId()
    const response = await participantRolesApi.setAccessLevels(id, roleId, {
      accessLevelIds: levelIds,
    })
    await load(id, true)
    return response.participantRole
  }

  /**
   * Набор пиктограмм роли целиком.
   *
   * ⚠️ Третья отдельная мутация, по той же причине, что и две предыдущие: три
   * набора живут у роли одновременно и правятся тремя разными панелями. Общий
   * запрос означал бы, что щелчок по пиктограмме переписывает зоны и уровни.
   *
   * Множество: порядок не значим, повторы схлопнёт сервер. Пустой массив —
   * законное значение, «снять все пиктограммы».
   */
  async function setPictograms(roleId: string, pictogramIds: string[]): Promise<ParticipantRole> {
    const id = requireEventId()
    const response = await participantRolesApi.setPictograms(id, roleId, { pictogramIds })
    await load(id, true)
    return response.participantRole
  }

  /**
   * Какие роли выдают эту пиктограмму.
   *
   * Считается ЗДЕСЬ, из уже загруженного справочника, по той же причине, что и
   * `rolesUsingZone`: каждая роль везёт свои `pictogramIds`, ответ у клиента уже
   * есть, и эндпоинт ради него был бы вторым источником правды.
   */
  function rolesUsingPictogram(pictogramId: string): ParticipantRole[] {
    if (!pictogramId) return []
    return items.value.filter((role) => role.pictogramIds.includes(pictogramId))
  }

  /**
   * Сколько ролей выдают этот уровень.
   *
   * Считается ЗДЕСЬ, из уже загруженного справочника, а не отдельным запросом —
   * по той же причине, что и `rolesUsingZone`: каждая роль везёт свои
   * `accessLevelIds`, ответ у клиента уже есть, и эндпоинт ради него был бы
   * вторым источником правды, расходящимся с первым.
   */
  function rolesUsingLevel(levelId: string): ParticipantRole[] {
    if (!levelId) return []
    return items.value.filter((role) => role.accessLevelIds.includes(levelId))
  }

  /**
   * ⚠️ У участников с этой ролью ссылка **обнуляется** (`ON DELETE SET NULL`):
   * сами участники остаются, но роль у них становится «не назначена».
   * Подтверждение обязано это сказать — иначе организатор узнаёт о пустой графе
   * из распечатанного стартового протокола.
   */
  async function remove(roleId: string): Promise<ParticipantRole> {
    const id = requireEventId()
    const response = await participantRolesApi.remove(id, roleId)
    await load(id, true)
    return response.participantRole
  }

  function requireEventId(): string {
    const id = eventId.value
    if (!id) throw new Error('[participantRoles] мутация вне скоупа мероприятия')
    return id
  }

  /* ───────────────────────── $reset ──────────────────────── */

  function $reset(): void {
    controller?.abort()
    controller = null

    eventId.value = null
    items.value = []
    loading.value = false
    error.value = null
    ready.value = false
  }

  return {
    // state
    eventId,
    items,
    loading,
    error,
    ready,
    // getters
    isEmpty,
    byId,
    nameOf,
    rolesUsingZone,
    rolesUsingLevel,
    rolesUsingPictogram,
    // actions
    load,
    create,
    update,
    setAccessZones,
    setAccessLevels,
    setPictograms,
    remove,
    $reset,
  }
})
